import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { Container, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import { ShieldAlert, UserPlus, CheckCircle } from 'lucide-react';

async function hashPassword(password) {
  const msgBuffer = new TextEncoder().encode(password);
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Tela de acesso inicial (root).
// Esta tela só tem efeito enquanto NÃO existir nenhum administrador
// cadastrado na base. Assim que o primeiro admin for criado, o acesso
// "root" perde automaticamente a função: qualquer novo acesso a esta
// URL é redirecionado para o login normal, pois a verificação abaixo
// (snapshot.empty) deixa de ser verdadeira.
const PrimeiroAcesso = () => {
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const verificar = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'admins'));
        if (!snapshot.empty) {
          // Já existe pelo menos um admin: o acesso root não é mais válido.
          navigate('/area-interna-gestao');
          return;
        }
        setChecking(false);
      } catch (err) {
        console.error(err);
        setChecking(false);
      }
    };
    verificar();
  }, [navigate]);

  const handleCreateFirstAdmin = async (e) => {
    e.preventDefault();
    setError('');

    if (senha.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres.');
      return;
    }
    if (senha !== confirmarSenha) {
      setError('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      // Confirma novamente, no momento do envio, que ninguém criou
      // um admin nesse meio tempo (evita duplicidade em caso de dois
      // usuários acessando essa tela ao mesmo tempo).
      const snapshot = await getDocs(collection(db, 'admins'));
      if (!snapshot.empty) {
        setError('Um administrador já foi cadastrado por outra pessoa. Você será redirecionado ao login.');
        setLoading(false);
        setTimeout(() => navigate('/area-interna-gestao'), 2000);
        return;
      }

      const hashed = await hashPassword(senha);
      await addDoc(collection(db, 'admins'), {
        email: email.toLowerCase().trim(),
        password: hashed,
        createdAt: new Date(),
      });

      setSuccess(true);
    } catch (err) {
      setError('Erro ao criar o administrador. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <div
        className="vh-100 d-flex align-items-center justify-content-center"
        style={{ backgroundColor: '#121212' }}
      >
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  if (success) {
    return (
      <div className="vh-100 d-flex align-items-center" style={{ backgroundColor: '#121212' }}>
        <Container>
          <div className="d-flex justify-content-center">
            <Card
              className="p-4 shadow-lg bg-dark text-white border-0 text-center"
              style={{ maxWidth: '460px', width: '100%' }}
            >
              <CheckCircle size={48} color="#FF8C00" className="mx-auto mb-3" />
              <h4 style={{ color: '#FF8C00' }}>Administrador criado com sucesso!</h4>
              <p className="text-muted mt-2 mb-4">
                O acesso root que você usou agora está desativado. A partir de agora,
                use o e-mail e a senha que você acabou de cadastrar para entrar no sistema.
              </p>
              <Button
                variant="warning"
                className="w-100 fw-bold"
                style={{ backgroundColor: '#FF8C00', border: 'none', color: 'black' }}
                onClick={() => navigate('/area-interna-gestao')}
              >
                Ir para o Login
              </Button>
            </Card>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="vh-100 d-flex align-items-center" style={{ backgroundColor: '#121212' }}>
      <Container>
        <div className="d-flex justify-content-center">
          <Card
            className="p-4 shadow-lg bg-dark text-white border-0"
            style={{ maxWidth: '480px', width: '100%' }}
          >
            <div className="text-center mb-3">
              <ShieldAlert size={40} color="#FF8C00" />
              <h4 className="mt-2 fw-bold" style={{ color: '#FF8C00' }}>
                Acesso Inicial do Sistema
              </h4>
            </div>

            <Alert variant="warning" className="text-black">
              <strong>Por que estou vendo esta tela?</strong>
              <p className="mb-2 mt-2">
                O sistema identificou que ainda <strong>não existe nenhum administrador
                cadastrado</strong>. Por isso, você está usando um acesso temporário e
                especial, chamado de <strong>acesso root</strong>, cuja única função é
                permitir o cadastro do primeiro administrador do sistema.
              </p>
              <p className="mb-0">
                Assim que você cadastrar o primeiro administrador abaixo, este acesso
                root deixa de funcionar automaticamente. A partir daí, o login normal
                (com e-mail e senha do administrador) passa a ser o único caminho de
                entrada no sistema.
              </p>
            </Alert>

            {error && <Alert variant="danger">{error}</Alert>}

            <Form onSubmit={handleCreateFirstAdmin}>
              <Form.Group className="mb-3">
                <Form.Label>E-mail do administrador</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="admin@email.com"
                  className="bg-secondary text-white border-0"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Senha</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Mínimo 6 caracteres"
                  className="bg-secondary text-white border-0"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Confirmar Senha</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Repita a senha"
                  className="bg-secondary text-white border-0"
                  value={confirmarSenha}
                  onChange={(e) => setConfirmarSenha(e.target.value)}
                  required
                />
              </Form.Group>

              <Button
                variant="warning"
                type="submit"
                className="w-100 fw-bold"
                style={{ backgroundColor: '#FF8C00', color: 'black' }}
                disabled={loading}
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <><UserPlus size={18} className="me-2" /> CRIAR PRIMEIRO ADMINISTRADOR</>
                )}
              </Button>
            </Form>
          </Card>
        </div>
      </Container>
    </div>
  );
};

export default PrimeiroAcesso;